// https://fontawesome.com/v5/docs/web/use-with/react
import styles from "./NoteFile.module.css";
import Figure from "react-bootstrap/Figure";
import FigureCaption from "react-bootstrap/FigureCaption";
import {Button, Col, Row} from "react-bootstrap";
import {NoteFileListItemInterface} from "./NoteFileListItemInterface";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCopy, faCheck} from "@fortawesome/free-solid-svg-icons";
import {useCopyToClipboard} from "usehooks-ts";
import {useEffect, useState} from "react";

export const NoteFileListItem = (props: NoteFileListItemInterface) => {
    const [_value, copy] = useCopyToClipboard();
    const [isCopied, setIsCopied] = useState<boolean>(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const apiBaseUrl = process.env.REACT_APP_API_URL ?? "";
    const fileUrl = `${apiBaseUrl}/api/v1/notes/${props.noteId}/files/${props.file.id}`;
    const isImage = props.file.mimeType?.startsWith("image/");

    useEffect(() => {
        if (!isImage) {
            return;
        }

        let objectUrl: string | null = null;
        let isMounted = true;

        const loadImage = async () => {
            const token = await props.getAccessToken();

            const response = await fetch(fileUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to load note file image");
            }

            const blob = await response.blob();
            objectUrl = URL.createObjectURL(blob);

            if (isMounted) {
                setPreviewUrl(objectUrl);
            }
        };

        loadImage();

        return () => {
            isMounted = false;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [fileUrl, isImage, props]);

    const onCopy = async () => {
        setIsCopied(true);

        const markdown = isImage
            ? `![${props.file.name}](${fileUrl})`
            : `[${props.file.name}](${fileUrl})`;

        await copy(markdown);

        setTimeout(() => {
            setIsCopied(false);
        }, 2000);
    };

    const onDownload = async () => {
        const token = await props.getAccessToken();

        const response = await fetch(fileUrl, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error("Failed to download file");
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = props.file.name;
        document.body.appendChild(link);
        link.click();
        link.remove();

        URL.revokeObjectURL(objectUrl);
    };

    return (
        <tr>
            <td className={styles.imageTableData}>
                <Figure>
                    {isImage ? (
                        previewUrl ? (
                            <a href={fileUrl} target="_blank" rel="noreferrer">
                                <Figure.Image
                                    src={previewUrl}
                                    alt={props.file.name}
                                    className={styles.previewImage}
                                />
                            </a>
                        ) : null
                    ) : (
                        <Button variant="secondary" size="sm" onClick={onDownload}>
                            Download {props.file.name}
                        </Button>
                    )}

                    <FigureCaption>
                        <Row>
                            <Col xs={12}>
                                {props.file.name}
                                <span>&nbsp;&nbsp;</span>

                                <button
                                    className={styles.copyButton}
                                    onClick={onCopy}
                                    title="Copy markdown"
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={isCopied ? faCheck : faCopy}/>
                                </button>
                            </Col>

                            <Col>
                                <Button
                                    variant="danger"
                                    onClick={() => props.deleteAction(props.file)}
                                >
                                    Delete
                                </Button>
                            </Col>
                        </Row>
                    </FigureCaption>
                </Figure>
            </td>
        </tr>
    );
};
